/**
 * TransferBondNotificationsModal
 * Shows transfer bond notifications grouped by agency with clear/clear all
 * Replaces the legacy notificationModal.jsx with Oath styling
 */

import { useState } from 'react';
import { toast } from 'react-toastify';
import { BellIcon } from '@heroicons/react/24/outline';
import { clearNotification, clearAllNotifications } from '../../../services/oathNotificationService';
import { useOathNotifications } from '../../../services/oathNotificationContext';
import { OathModal, OathButton } from '../../shared/OathUI';

// Keywords to bold in notification messages
const BOLD_KEYWORDS = ['Accepted', 'Declined', 'posted', 'Cancelled', 'expired'];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMessageParts = (message) => {
  if (!message) return null;
  const words = message.split(/(\s+)/);
  return words.map((word, idx) => {
    const clean = word.replace(/[^\w]/g, '');
    if (BOLD_KEYWORDS.includes(clean)) {
      return <strong key={idx} className="text-gray-900 dark:text-white">{word}</strong>;
    }
    return word;
  });
};

const getStatusColor = (typeName) => {
  if (!typeName) return 'text-gray-600 dark:text-gray-400';
  const lower = typeName.toLowerCase();
  if (lower.includes('accept')) return 'text-green-600 dark:text-green-400';
  if (lower.includes('decline') || lower.includes('cancel') || lower.includes('expire'))
    return 'text-red-600 dark:text-red-400';
  if (lower.includes('post') || lower.includes('new request'))
    return 'text-primary dark:text-primary-light';
  if (lower.includes('quote')) return 'text-amber-600 dark:text-amber-400';
  return 'text-gray-600 dark:text-gray-400';
};

const TransferBondNotificationsModal = ({ isOpen, onClose }) => {
  const { notifications, setNotifications, refresh } = useOathNotifications();
  const [clearingId, setClearingId] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);

  // Only show transfer bond notifications (exclude large bonds, etc.)
  const tbNotifications = notifications.filter((n) => n.contextTypeCode !== 'LB');

  // Group notifications by agency
  const grouped = tbNotifications.reduce((acc, n) => {
    if (n.agencyName && n.agencyID) {
      const key = `${n.agencyName} (${n.agencyID})`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(n);
    }
    return acc;
  }, {});

  const handleClear = async (id) => {
    setClearingId(id);
    const response = await clearNotification(id);
    if (response.success) {
      setNotifications((prev) => prev.filter((n) => n.notificationID !== id));
      toast.success('Notification cleared.');
    } else {
      toast.error('Failed to clear notification.');
    }
    setClearingId(null);
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    const response = await clearAllNotifications();
    if (response.success) {
      setNotifications([]);
      toast.success('All notifications cleared.');
    } else {
      toast.error('Failed to clear notifications.');
    }
    setClearingAll(false);
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Notifications (${tbNotifications.length})`}
      size="lg"
      footer={
        tbNotifications.length > 0 && (
          <div className="flex justify-end w-full">
            <OathButton
              variant="outline"
              onClick={handleClearAll}
              loading={clearingAll}
            >
              Clear All
            </OathButton>
          </div>
        )
      }
    >
      {tbNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
          <BellIcon className="h-12 w-12 mb-3" />
          <p className="text-sm">No notifications at this time.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([agencyKey, agencyNotifs]) => (
            <div key={agencyKey}>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                {agencyKey}
              </h4>
              <div className="space-y-3">
                {agencyNotifs.map((n) => (
                  <div
                    key={n.notificationID}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-700/50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Ref# and type */}
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-sm font-bold text-gray-900 dark:text-white">
                            Ref# {n.keyValue}
                          </span>
                          <span className={`text-xs font-medium ${getStatusColor(n.notificationTypeName)}`}>
                            {n.notificationTypeName}
                          </span>
                        </div>

                        {/* Timestamp */}
                        <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                          {formatDate(n.createdAt)}
                        </p>

                        {/* Message with bolded keywords */}
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                          {formatMessageParts(n.message)}
                        </p>
                      </div>

                      {/* Clear button */}
                      <button
                        onClick={() => handleClear(n.notificationID)}
                        disabled={clearingId === n.notificationID}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {clearingId === n.notificationID ? 'Clearing...' : 'Clear'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </OathModal>
  );
};

export default TransferBondNotificationsModal;
